import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useIsIntersecting } from 'utils/hooks/use-is-intersecting'

/** A hidden anchor element */
const InfiniteScrollAnchor = styled.div.attrs({ 'data-test-id': 'infinite-scroll' })({
  height: 1,
  marginTop: -1,
  visibility: 'hidden',
})

interface InfiniteScrollProps extends IntersectionObserverInit {
  children: ReactNode
  disabled?: boolean
  /** A callback for when the bottom of `children` is visible */
  onScrolledToBottom: () => void | Promise<void>
}

/**
 * A render component for handling infinite scroll. Given a long `children`
 * node, the `onScrolledToBottom` callback will be fired when its bottom is
 * visible, according to an `IntersectionObserver` targeting a 1px tall
 * hidden div. It's possible to adjust the observer's settings with the
 * `root`, `rootMargin`, and `threshold` props.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 *
 * @example
 * <InfiniteScroll onScrolledToBottom={loadMoreProducts}>
 *     {products.map((product) => <Product key={product.ean} product={product} />)}
 * </InfiniteScroll>
 * {!!loadingMoreProducts && <LoadingIndicator />}
 */
export const InfiniteScroll: FC<InfiniteScrollProps> = ({
  children,
  disabled,
  onScrolledToBottom,
  root,
  rootMargin,
  threshold,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const isIntersecting = useIsIntersecting(anchorRef, { disabled, root, rootMargin, threshold })

  const promise = useRef<Promise<void>>()
  useEffect(() => {
    if (promise.current || !isIntersecting) return

    const handler = async () => {
      await onScrolledToBottom()
      // Allow `useIsIntersecting` to update before clearing promise
      requestAnimationFrame(() => {
        promise.current = undefined
      })
    }

    promise.current = handler()
  }, [isIntersecting, onScrolledToBottom])

  return (
    <>
      {children}
      <InfiniteScrollAnchor ref={anchorRef} />
    </>
  )
}
